import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import ReactDOM from "react-dom";
import { useEffect, useMemo, useState } from "react";
import CloseIcon from "assets/icons/close.svg?react";
import TeamIcon from "assets/icons/team.svg?react";
import workspaceService from "api/workspaceService";
import { ENotificationType, notification } from "utils/notification";
import { useAuth } from "react-oidc-context";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import { EUserType } from "dto/User/EUserType";
import { useTranslation } from "react-i18next";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

// Dodanie członka zespołu = ZAPROSZENIE (jedno źródło prawdy o osobach: WorkspaceMembers).
// Komórka organizacyjna i stanowisko trafiają od razu na członkostwo (InviteRequest.StructureId /
// PositionTitle) — bez osobnej listy „osób ze schematu".

const AddWorker: FC<Props> = ({
  roles,
  structure,
  toggle,
  workspaceId,
  refetch,
  initialName,
  initialEmail,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === EUserType.LegalPerson;

  const { mutate: invite, isLoading } = workspaceService.useInviteToWorkspace();

  const [name, setName] = useState(initialName ?? "");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [selectedRole, setSelectedRole] = useState("");
  const [structureId, setStructureId] = useState("");
  const [positionTitle, setPositionTitle] = useState("");

  const mappedRoles = useMemo(
    () =>
      roles
        .filter((role) => role.StaticType !== "owner" || isLegalPerson)
        .map((role) => ({ value: role.Id, label: role.Name })) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles],
  );

  // Komórki w kolejności drzewa z wcięciem wg głębokości.
  const units = useMemo(() => {
    const items = structure ?? [];
    const out: { id: string; name: string; depth: number }[] = [];
    const walk = (parentId: string | null, depth: number) => {
      items
        .filter((i) => (i.ParentId ?? null) === parentId)
        .sort((a, b) => a.Name.localeCompare(b.Name, "pl"))
        .forEach((i) => {
          out.push({
            id: i.Id,
            name: i.Symbol ? `${i.Symbol} · ${i.Name}` : i.Name,
            depth,
          });
          walk(i.Id, depth + 1);
        });
    };
    walk(null, 0);
    return out;
  }, [structure]);

  useEffect(() => {
    if (mappedRoles.length > 0 && !selectedRole) {
      setSelectedRole(mappedRoles[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedRoles]);

  const canInvite = !!email.trim() && !!name.trim() && !!workspaceId && !!selectedRole;

  const handleInvite = () => {
    invite(
      {
        RoleId: selectedRole,
        WorkspaceId: workspaceId,
        Email: email.replaceAll(" ", ""),
        Name: name.trim(),
        StructureId: structureId || null,
        PositionTitle: positionTitle.trim() || null,
      },
      {
        onSuccess: () => {
          refetch();
          onSuccess?.();
          toggle();
        },
        onError: (e: {
          response?: { data?: { errorCodes?: Array<{ message: string }> } };
        }) => {
          notification({
            text:
              e.response?.data?.errorCodes?.[0]?.message ??
              t("errors.somethingWentWrong"),
            title: t("errors.somethingWentWrongTitle"),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  return ReactDOM.createPortal(
    <div className={styles.modal}>
      <div className={styles.window}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>
              <TeamIcon />
              {t("settings.addWorkerTitle")}
            </div>
            <CloseIcon onClick={toggle} className={styles.icon} />
          </div>

          <fieldset className={styles.group}>
            <legend className={styles.groupLabel}>Dane osoby</legend>
            <div className={styles.fields}>
              <TextField
                variant="standard"
                label="Imię i Nazwisko"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <TextField
                variant="standard"
                label="E-mail (zaproszenie)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
              <FormControl variant="standard" fullWidth>
                <InputLabel>{t("forms.role")}</InputLabel>
                <Select
                  variant="standard"
                  value={selectedRole}
                  label={t("forms.role")}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={mappedRoles.length === 0}
                >
                  {mappedRoles.length === 0 && (
                    <MenuItem value="" disabled>
                      <em>Brak ról w zespole</em>
                    </MenuItem>
                  )}
                  {mappedRoles.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </fieldset>

          <fieldset className={styles.group}>
            <legend className={styles.groupLabel}>Miejsce w strukturze (opcjonalne)</legend>
            <div className={styles.fields}>
              <FormControl variant="standard" fullWidth>
                <InputLabel>Komórka organizacyjna</InputLabel>
                <Select
                  variant="standard"
                  value={structureId}
                  label="Komórka organizacyjna"
                  onChange={(e) => setStructureId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Brak przypisania</em>
                  </MenuItem>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <span style={{ paddingLeft: u.depth * 16 }}>{u.name}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {units.length === 0 && (
                <div className={styles.structureEmpty}>
                  Brak komórek — zdefiniuj je w zakładce Zespół / Struktura.
                </div>
              )}
              <TextField
                variant="standard"
                label="Stanowisko"
                value={positionTitle}
                onChange={(e) => setPositionTitle(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
            </div>
          </fieldset>

          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={toggle}>
              Anuluj
            </button>
            <div className={styles.actionsRight}>
              <button
                className={styles.inviteBtn}
                disabled={!canInvite || isLoading}
                onClick={handleInvite}
              >
                Zaproś do zespołu
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddWorker;
